import { db, auth } from '../firebase';
import { collection, addDoc, query, where, getDocs, orderBy, limit, Timestamp } from 'firebase/firestore';

/**
 * Search service to track search queries and provide analytics
 */
const searchService = {
  /**
   * Record a search query in Firestore
   * @param {string} searchQuery - The search query text
   * @param {Array} tags - Optional array of tags associated with the search
   * @param {Object} results - Optional search results data
   * @returns {Promise} - Promise resolving to the document reference
   */
  trackSearchQuery: async (searchQuery, tags = [], results = null) => {
    try {
      // Validate input
      if (!searchQuery || typeof searchQuery !== 'string') {
        console.error('Invalid search query:', searchQuery);
        return null;
      }
      
      const user = auth.currentUser;
      
      // Create search data object
      const searchData = {
        query: searchQuery.trim(),
        timestamp: Timestamp.now(),
        userId: user ? user.uid : null,
        isAuthenticated: !!user,
        tags: Array.isArray(tags) ? tags : [], // Ensure tags is always an array
        resultCount: results ? (Array.isArray(results) ? results.length : 0) : 0,
      };
      
      // Add user information if available
      if (user) {
        searchData.userName = user.displayName || '';
        searchData.userEmail = user.email || '';
      }
      
      // Store in Firestore
      const docRef = await addDoc(collection(db, 'searchQueries'), searchData);
      return docRef;
    } catch (error) {
      console.error('Error tracking search query:', error);
      return null;
    }
  },
  
  /**
   * Get recent searches for the current user
   * @param {number} limit - Maximum number of searches to return
   * @returns {Promise<Array>} - Promise resolving to array of search queries
   */
  getUserSearchHistory: async (limitCount = 10) => {
    try {
      const user = auth.currentUser;
      if (!user) return [];
      
      const searchesRef = collection(db, 'searchQueries');
      const userSearchesQuery = query(
        searchesRef,
        where('userId', '==', user.uid),
        orderBy('timestamp', 'desc'),
        limit(limitCount)
      );
      
      const snapshot = await getDocs(userSearchesQuery);
      return snapshot.docs.map(doc => ({
        id: doc.id,
        ...doc.data(),
        timestamp: doc.data().timestamp.toDate()
      }));
    } catch (error) {
      console.error('Error getting user search history:', error);
      return [];
    }
  },
  
  /**
   * Get trending searches within a time period
   * @param {number} days - Number of days to look back
   * @param {number} limitCount - Maximum number of trending searches to return
   * @returns {Promise<Array>} - Promise resolving to array of trending searches
   */
  getTrendingSearches: async (days = 7, limitCount = 10) => {
    try {
      const searchesRef = collection(db, 'searchQueries');
      
      // Calculate date limit
      const dateLimit = new Date();
      dateLimit.setDate(dateLimit.getDate() - days);
      
      const searchQuery = query(
        searchesRef,
        where('timestamp', '>=', Timestamp.fromDate(dateLimit)),
        orderBy('timestamp', 'desc')
      );
      
      const snapshot = await getDocs(searchQuery);
      
      // Count occurrences of each search term
      const searchCounts = {};
      snapshot.forEach(doc => {
        const data = doc.data();
        if (data.query) {
          const term = data.query.toLowerCase().trim();
          if (term) {
            searchCounts[term] = (searchCounts[term] || 0) + 1;
          }
        }
      });
      
      // Convert to array, sort by count, and limit
      return Object.entries(searchCounts)
        .map(([term, count]) => ({ term, count }))
        .sort((a, b) => b.count - a.count)
        .slice(0, limitCount);
    } catch (error) {
      console.error('Error getting trending searches:', error);
      return [];
    }
  },
  
  /**
   * Get search statistics for the platform
   * @returns {Promise<Object>} - Promise resolving to search statistics
   */
  getSearchStatistics: async () => {
    try {
      const searchesRef = collection(db, 'searchQueries');
      
      // Today's searches
      const today = new Date();
      today.setHours(0, 0, 0, 0);
      const todayQuery = query(
        searchesRef,
        where('timestamp', '>=', Timestamp.fromDate(today))
      );
      const todaySnapshot = await getDocs(todayQuery);
      
      // This week's searches
      const weekAgo = new Date();
      weekAgo.setDate(weekAgo.getDate() - 7);
      const weekQuery = query(
        searchesRef,
        where('timestamp', '>=', Timestamp.fromDate(weekAgo))
      );
      const weekSnapshot = await getDocs(weekQuery);
      
      // Total searches
      const totalQuery = query(searchesRef);
      const totalSnapshot = await getDocs(totalQuery);
      
      return {
        todayCount: todaySnapshot.size,
        weekCount: weekSnapshot.size,
        totalCount: totalSnapshot.size
      };
    } catch (error) {
      console.error('Error getting search statistics:', error);
      return {
        todayCount: 0,
        weekCount: 0,
        totalCount: 0
      };
    }
  },
  
  /**
   * Get popular tags from searches
   * @param {string} timeFrame - Time frame: 'day', 'week', 'month', 'year'
   * @param {number} limitCount - Maximum number of tags to return
   * @returns {Promise<Array>} - Promise resolving to array of popular tags
   */
  getPopularTags: async (timeFrame = 'week', limitCount = 10) => {
    try {
      // Calculate date limit based on timeFrame
      const dateLimit = new Date();
      switch (timeFrame) {
        case 'day':
          dateLimit.setHours(0, 0, 0, 0);
          break;
        case 'week':
          dateLimit.setDate(dateLimit.getDate() - 7);
          break;
        case 'month':
          dateLimit.setMonth(dateLimit.getMonth() - 1);
          break;
        case 'year':
          dateLimit.setFullYear(dateLimit.getFullYear() - 1);
          break;
        default:
          dateLimit.setDate(dateLimit.getDate() - 7);
      }
      
      // Get searches within the time frame
      const searchesRef = collection(db, 'searchQueries');
      const timeFrameQuery = query(
        searchesRef,
        where('timestamp', '>=', Timestamp.fromDate(dateLimit))
      );
      
      const snapshot = await getDocs(timeFrameQuery);
      
      // Count occurrences of each tag
      const tagCounts = {};
      snapshot.forEach(doc => {
        const data = doc.data();
        if (data.tags && Array.isArray(data.tags)) {
          data.tags.forEach(tag => {
            if (tag) {
              tagCounts[tag] = (tagCounts[tag] || 0) + 1;
            }
          });
        }
      });
      
      // Convert to array, sort by count, and return top N results
      return Object.entries(tagCounts)
        .map(([tag, count]) => ({ tag, count }))
        .sort((a, b) => b.count - a.count)
        .slice(0, limitCount);
    } catch (error) {
      console.error('Error getting popular tags:', error);
      return [];
    }
  }
};

export default searchService; 